"use strict";
/**
 * Two-Stage Click Handler
 *
 * Implements a "click-to-arm, click-to-confirm" pattern on order buttons
 * to prevent accidental order placement. When two-stage confirmation is
 * enabled the first click arms the button (adds a `confirm-pending` CSS
 * class) and starts a timeout; a second click within the timeout window
 * actually fires the order. When confirmation is disabled the order is
 * placed on the first click.
 *
 * Used by both the standard Buy/Sell buttons and the Baseball Mode buttons.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.recordArmedMarket = recordArmedMarket;
exports.armedMarketMatchesCurrent = armedMarketMatchesCurrent;
exports.disarmAllOrderButtons = disarmAllOrderButtons;
exports.registerTwoStageMarketGuard = registerTwoStageMarketGuard;
exports.confirmArmedMarketOrDisarm = confirmArmedMarketOrDisarm;
exports.attachTwoStageClick = attachTwoStageClick;
exports.attachTwoStageExecution = attachTwoStageExecution;
const state_1 = require("../../state");
const ui_1 = require("../../ui");
const order_placement_1 = require("./order-placement");
// ---------------------------------------------------------------------------
// Market guard: an armed button must only fire on the market it was armed on.
// Shared by the standard buttons here and the Baseball Mode buttons in
// baseball-controls.ts.
// ---------------------------------------------------------------------------
/** Market that was selected when a button was last armed. */
let armedMarketId = null;
let marketGuardRegistered = false;
/** Record the currently selected market as the one the armed button targets. */
function recordArmedMarket() {
    armedMarketId = (0, state_1.getState)().selectedMarket?.id ?? null;
}
/** True if the selected market is still the one the button was armed on. */
function armedMarketMatchesCurrent() {
    return armedMarketId === ((0, state_1.getState)().selectedMarket?.id ?? null);
}
/** Disarm every armed order button and clear the armed-market record. */
function disarmAllOrderButtons() {
    armedMarketId = null;
    document
        .querySelectorAll(".confirm-pending")
        .forEach((el) => el.classList.remove("confirm-pending"));
}
/**
 * Subscribe to state changes and disarm all order buttons whenever the
 * selected market changes. Idempotent; returns an unsubscribe function.
 */
function registerTwoStageMarketGuard() {
    if (marketGuardRegistered) {
        return () => { };
    }
    marketGuardRegistered = true;
    let lastMarketId = (0, state_1.getState)().selectedMarket?.id ?? null;
    const unsubscribe = (0, state_1.subscribe)((state) => {
        const currentId = state.selectedMarket?.id ?? null;
        if (currentId !== lastMarketId) {
            lastMarketId = currentId;
            disarmAllOrderButtons();
        }
    });
    return () => {
        marketGuardRegistered = false;
        unsubscribe();
    };
}
/**
 * Stage-2 safety check shared by all two-stage buttons: if the market changed
 * while the button was armed, disarm instead of placing the order.
 * Returns true when it is safe to proceed.
 */
function confirmArmedMarketOrDisarm(btn) {
    if (armedMarketMatchesCurrent()) {
        return true;
    }
    btn.classList.remove("confirm-pending");
    armedMarketId = null;
    (0, ui_1.showOrderError)("Market changed since this button was armed — order NOT placed. Re-arm to confirm.");
    return false;
}
/**
 * Attach a two-stage click handler to an order button.
 *
 * @param btn              - The button element (may be null if the DOM element doesn't exist)
 * @param side             - Market side this button acts on ("yes" or "no")
 * @param action           - Trade action ("buy" or "sell")
 * @param armTimeoutId     - Shared mutable ref holding the current arm-timeout ID
 * @param allButtons       - All buttons that participate in mutual exclusion (reset on arm)
 * @param isConfirmRequired - Callback returning whether two-stage confirmation is active
 * @param slippageOverride  - Optional fixed slippage for baseball-mode buy buttons
 * @param buttonId          - Logical order-button ID; buy clicks pass it through
 *                            so additional-market assignments on this button fire
 */
function attachTwoStageClick(btn, side, action, armTimeoutId, allButtons, isConfirmRequired, slippageOverride, buttonId) {
    attachTwoStageExecution(btn, {
        armTimeoutId,
        resetButtons: [allButtons],
        isConfirmRequired,
        // Block the click if manual-price mode is active (but NOT when a sport
        // layout is showing, which uses per-button pricing) and no price is
        // selected. Under forceDefaultButtons no sport layout is active, so
        // the visible traditional buttons get the normal manual-price guard.
        beforeClick: () => {
            const { tradingSettings } = (0, state_1.getState)();
            if (tradingSettings.manualPriceMode === true &&
                !(0, ui_1.isSportLayoutActive)(tradingSettings) &&
                !(0, order_placement_1.hasSelectedManualPrice)(side)) {
                (0, ui_1.showOrderError)(`Select a manual ${side.toUpperCase()} price before placing an order`);
                return false;
            }
            return true;
        },
        onExecute: () => (0, order_placement_1.handlePlaceOrder)(side, action, slippageOverride, buttonId),
    });
}
/**
 * Generic click-to-arm / click-to-confirm behavior shared by the standard
 * Buy/Sell buttons and the Baseball Mode buttons. Owns the arm CSS class,
 * the arm timeout, and the market guard (an armed button only fires on the
 * market it was armed on).
 */
function attachTwoStageExecution(btn, options) {
    if (!btn)
        return;
    const { armTimeoutId, resetButtons, isConfirmRequired, onExecute } = options;
    btn.addEventListener("click", () => {
        if (options.beforeClick && !options.beforeClick()) {
            return;
        }
        // When confirmation is disabled, execute immediately
        if (!isConfirmRequired()) {
            onExecute();
            if (armTimeoutId.value) {
                clearTimeout(armTimeoutId.value);
                armTimeoutId.value = null;
            }
            btn.classList.remove("confirm-pending");
            return;
        }
        const { tradingSettings } = (0, state_1.getState)();
        // Stage 1: Arm the button
        if (!btn.classList.contains("confirm-pending")) {
            // Reset all other buttons in every participating group
            resetButtons.forEach((group) => {
                group.forEach((b) => {
                    b?.classList.remove("confirm-pending");
                });
            });
            // Clear any existing timeout
            if (armTimeoutId.value) {
                clearTimeout(armTimeoutId.value);
                armTimeoutId.value = null;
            }
            // Arm this button, remembering which market it targets
            btn.classList.add("confirm-pending");
            recordArmedMarket();
            // Set timeout using latest state value
            const delay = (tradingSettings.orderTimeoutSeconds || 5) * 1000;
            armTimeoutId.value = window.setTimeout(() => {
                btn.classList.remove("confirm-pending");
                armTimeoutId.value = null;
            }, delay);
        }
        // Stage 2: Execute the order (only if still on the market it was armed on)
        else {
            if (armTimeoutId.value) {
                clearTimeout(armTimeoutId.value);
                armTimeoutId.value = null;
            }
            if (!confirmArmedMarketOrDisarm(btn)) {
                return;
            }
            onExecute();
            btn.classList.remove("confirm-pending");
        }
    });
}
